/**
 * Grading semantics (spec §4.4). Pure functions, no I/O, shared by the server
 * (authoritative grading) and the client (live echo, optimistic feedback).
 */

import type {
  CommonError,
  Localized,
  McQuestion,
  NumericQuestion,
  OpenQuestion,
  Question,
  SlipKind,
  Tolerance,
} from "../content/schema.js";

/* --------------------------- parseNumericAnswer --------------------------- */

/**
 * `unit` is present only when the student typed a trailing unit token
 * (trimmed, case preserved as typed — e.g. `"13.6 MeV"` → `unit: "MeV"`).
 * It is absent, not `""`, when nothing was typed.
 */
export type ParseResult = { value: number; unit?: string } | { error: "unparseable" };

/** Unicode characters that behave as a minus sign in student input. */
const MINUS_LIKE = /[−–—‒➖]/g;
/** Spaces (incl. thin / no-break) that may be used as digit groupers. */
const SPACE_LIKE = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;
/** `2,5 × 10^-3`, `6,63·10^-34`, `1.2*10^8`, `3x10^5`, `10^-3`. */
const POWER_OF_TEN = /(?:\s*[·×xX*⋅∙]\s*)?10\s*\^\s*\{?\s*([+-]?\d{1,4})\s*\}?/;
/** A mantissa with an optional `e` exponent. */
const MANTISSA = /^([+-]?(?:\d+(?:[.,]\d*)?|[.,]\d+)(?:[eE][+-]?\d{1,4})?)(.*)$/;

const UNPARSEABLE: ParseResult = { error: "unparseable" };

/**
 * Parses what a student typed into a plain number.
 *
 * Accepts: decimal comma or point (never both), unicode minus, spaces as digit
 * groupers, the exponent forms `e`/`E`/`·10^`/`×10^`/`x10^`/`*10^`/`10^`
 * (braces around the exponent optional) and one trailing unit token
 * (`13.6 eV`, `0.90c`, `1.54 Å`, `2 1/m`).
 */
export function parseNumericAnswer(input: string | null | undefined): ParseResult {
  if (input === null || input === undefined) return UNPARSEABLE;
  let s = String(input).replace(MINUS_LIKE, "-").replace(SPACE_LIKE, " ").replace(/⁄/g, "/").trim();
  if (s === "") return UNPARSEABLE;

  // Digit-grouping spaces: "1 240 000" → "1240000".
  // Keep the separator before a reciprocal unit: "2 1/m" is not "21/m".
  s = s.replace(/(\d) +(?=\d(?!\/))/g, "$1");

  // A string mixing both decimal separators is ambiguous and rejected.
  if (s.includes(",") && s.includes(".")) return UNPARSEABLE;

  // Normalise explicit powers of ten into an `e` exponent.
  const pow = POWER_OF_TEN.exec(s);
  if (pow && pow.index >= 0) {
    const before = s.slice(0, pow.index).trim();
    const after = s.slice(pow.index + pow[0].length);
    // "10^3" with nothing in front means 1 × 10^3.
    const mantissa = before === "" ? "1" : before;
    s = `${mantissa}e${pow[1]}${after}`;
  }

  const m = MANTISSA.exec(s.trim());
  if (!m) return UNPARSEABLE;
  const [, numberPart, restRaw] = m;
  const rest = restRaw.trim();
  if (rest !== "") {
    // One trailing unit token, with no spaces; a reciprocal may start with "1/".
    if (/\s/.test(rest)) return UNPARSEABLE;
    if (!/^(?:[\p{L}°%]|1\/[\p{L}])/u.test(rest)) return UNPARSEABLE;
    if (rest.length > 16) return UNPARSEABLE;
  }
  const value = Number(numberPart.replace(",", "."));
  if (!Number.isFinite(value)) return UNPARSEABLE;
  return rest === "" ? { value } : { value, unit: rest };
}

/* ------------------------------- tolerance -------------------------------- */

export interface NumericPartLike {
  answer: number;
  unit: string;
  tolerance: Tolerance;
}

const EPS = 1e-12;

/** Inclusive comparison against the part's tolerance. */
export function isNumericCorrect(value: number, part: NumericPartLike): boolean {
  if (!Number.isFinite(value)) return false;
  const t = part.tolerance;
  switch (t.type) {
    case "relative": {
      const allowed = Math.abs(part.answer) * t.value;
      return Math.abs(value - part.answer) <= allowed * (1 + EPS) + EPS * Math.abs(part.answer);
    }
    case "absolute":
      return Math.abs(value - part.answer) <= t.value * (1 + EPS);
    case "interval":
      return value >= t.min - EPS && value <= t.max + EPS;
  }
}

/* ------------------------------- near miss -------------------------------- */

export type NearMiss =
  | { kind: "power-of-ten"; exponent: number }
  | { kind: "unit"; from: string; to: string }
  /**
   * A unit was typed that is neither the part's unit (up to case) nor
   * convertible into it via `UNIT_GROUPS` — e.g. "kg" where "eV" is wanted.
   * Unlike the other two kinds this is not a near miss on the *value*; the
   * part is simply incorrect, and this only carries what the feedback needs
   * to say so (spec: numeric grading unit handling, review finding 3).
   */
  | { kind: "unit-mismatch"; typed: string; expected: string }
  /** The value is the answer with its sign flipped (value ≈ −answer, within tolerance). */
  | { kind: "sign" }
  /**
   * The value is the answer times a factor that is easy to drop or add (value ≈ answer × factor,
   * within tolerance): `2`, `0.5`, `"2pi"` (×2π) or `"1/2pi"` (÷2π, the h versus ħ slip).
   */
  | { kind: "factor"; factor: 2 | 0.5 | "2pi" | "1/2pi" };

/**
 * Unit conversion groups (spec §4.4b): near-miss detection, and converting a
 * typed unit into the part's unit before grading. Keys are canonical unit
 * notations — see `canonicalUnit`.
 */
// Powers used by wave functions, attenuation, density and position variance.
// Apply the exponent to the scale as well as the symbol (1 nm^-1 = 10^9 m^-1).
const LENGTH_SCALES: Record<string, number> = {
  m: 1,
  cm: 1e-2,
  mm: 1e-3,
  µm: 1e-6,
  nm: 1e-9,
  Å: 1e-10,
  pm: 1e-12,
  fm: 1e-15,
};
const LENGTH_POWERS: [string, number][] = [
  ["-3", -3],
  ["-5/2", -2.5],
  ["-3/2", -1.5],
  ["-1", -1],
  ["-1/2", -0.5],
  ["2", 2],
  ["3", 3],
];
const JULIAN_YEAR_SECONDS = 31_557_600;

const UNIT_GROUPS: Record<string, number>[] = [
  // energy, base J ("meV" listed so that it converts, never matches "MeV" by case)
  {
    J: 1,
    neV: 1.602176634e-28,
    meV: 1.602176634e-22,
    eV: 1.602176634e-19,
    keV: 1.602176634e-16,
    MeV: 1.602176634e-13,
    GeV: 1.602176634e-10,
  },
  // length, base m
  { ...LENGTH_SCALES, ly: 299_792_458 * JULIAN_YEAR_SECONDS },
  ...LENGTH_POWERS.map(([notation, exponent]) =>
    Object.fromEntries(
      Object.entries(LENGTH_SCALES).map(([unit, scale]) => [
        `${unit}^${notation}`,
        scale ** exponent,
      ]),
    ),
  ),
  // mass, base kg
  { kg: 1, g: 1e-3, u: 1.6605390666e-27 },
  // angle, base rad
  { rad: 1, "°": Math.PI / 180, deg: Math.PI / 180 },
  // speed, base m/s
  { "m/s": 1, c: 299_792_458 },
  // time, base s
  { s: 1, ms: 1e-3, µs: 1e-6, ns: 1e-9, ps: 1e-12, fs: 1e-15, min: 60, yr: JULIAN_YEAR_SECONDS },
  // frequency, base Hz
  { Hz: 1, "s^-1": 1, kHz: 1e3, MHz: 1e6, GHz: 1e9 },
  // voltage, base V ("MV" listed so that "mv" is ambiguous, never silently mV)
  { V: 1, mV: 1e-3, kV: 1e3, MV: 1e6 },
  // current, base A
  { A: 1, mA: 1e-3, µA: 1e-6, nA: 1e-9 },
  // Pressure, base Pa. Include mPa so ambiguous "mpa" cannot silently mean MPa.
  { Pa: 1, mPa: 1e-3, kPa: 1e3, MPa: 1e6, GPa: 1e9, bar: 1e5 },
  // Momentum, action/angular momentum, and moment of inertia.
  {
    "kg·m/s": 1,
    "meV/c": 1.602176634e-22 / 299_792_458,
    "eV/c": 1.602176634e-19 / 299_792_458,
    "keV/c": 1.602176634e-16 / 299_792_458,
    "MeV/c": 1.602176634e-13 / 299_792_458,
  },
  { "J·s": 1, "eV·s": 1.602176634e-19 },
  { "kg·m^2": 1, "g·cm^2": 1e-7 },
  // Named derived units and equivalent SI forms used by the live bank.
  { N: 1, "kg·m/s^2": 1 },
  { "N/m": 1, "kg/s^2": 1 },
  { T: 1, "kg/(A·s^2)": 1 },
  { W: 1, "J/s": 1 },
  { "W/m^3": 1, "J/(s·m^3)": 1 },
  { "J·m^-3·Hz^-1": 1, "J·s/m^3": 1 },
  // dimensionless: a percentage typed into a plain-number part
  { "": 1, "%": 1e-2 },
];

/** Exact symbolic aliases; multiplication boundaries are never discarded. */
const UNIT_ALIASES: Record<string, string> = {
  um: "µm",
  us: "µs",
  uA: "µA",
  "m·s^-1": "m/s",
  "m/s^1": "m/s",
  "m·s^-2": "m/s^2",
  "kg·m^2/s^2": "J",
  "kg·m^2·s^-2": "J",
  "N/m^2": "Pa",
  "N·m^-2": "Pa",
  "kg·m^-1·s^-2": "Pa",
  "N·s": "kg·m/s",
  "kg·m·s^-1": "kg·m/s",
  "kg·m^2/s": "J·s",
  "kg·m^2·s^-1": "J·s",
  "kg·m·s^-2": "kg·m/s^2",
  "kg·s^-2": "kg/s^2",
  "kg·s^-2·A^-1": "kg/(A·s^2)",
  "kg·m^2·s^-3": "W",
  "J·m^-3·s^-1": "W/m^3",
  "kg·m^-1·s^-3": "W/m^3",
  "J·s·m^-3": "J·s/m^3",
  "kg·m^-1·s^-1": "J·s/m^3",
  "J/C": "V",
  "kg·m^2·s^-3·A^-1": "V",
  // Unambiguous compact products accepted by the existing input contract.
  Js: "J·s",
  eVs: "eV·s",
  "kgm/s": "kg·m/s",
  "kgm^2": "kg·m^2",
  "gcm^2": "g·cm^2",
  "kgm/s^2": "kg·m/s^2",
  "kg/(As^2)": "kg/(A·s^2)",
  "J/(sm^3)": "J/(s·m^3)",
  "Jm^-3Hz^-1": "J·m^-3·Hz^-1",
  "Js/m^3": "J·s/m^3",
  "kgm^2/s^2": "J",
  "kgm^2s^-2": "J",
  "Nm^-2": "Pa",
  "kgm^-1s^-2": "Pa",
  Ns: "kg·m/s",
  "kgms^-1": "kg·m/s",
  "kgm^2/s": "J·s",
  "kgm^2s^-1": "J·s",
  "kgms^-2": "kg·m/s^2",
  "kgs^-2": "kg/s^2",
  "kgs^-2A^-1": "kg/(A·s^2)",
  "kgm^2s^-3": "W",
  "Jm^-3s^-1": "W/m^3",
  "kgm^-1s^-3": "W/m^3",
  "Jsm^-3": "J·s/m^3",
  "kgm^-1s^-1": "J·s/m^3",
  "kgm^2s^-3A^-1": "V",
};

/** Word aliases may fold case; symbolic prefixes must keep their case. */
const UNIT_WORD_ALIASES: Record<string, string> = {
  angstrom: "Å",
  degrees: "°",
  degree: "°",
  grader: "°",
  joule: "J",
  år: "yr",
  ljusår: "ly",
};

const SUPERSCRIPTS: Record<string, string> = {
  "⁰": "0",
  "¹": "1",
  "²": "2",
  "³": "3",
  "⁴": "4",
  "⁵": "5",
  "⁶": "6",
  "⁷": "7",
  "⁸": "8",
  "⁹": "9",
  "⁻": "-",
  "⁺": "+",
  // superscript fraction slash, as rendered by the client's formatUnit
  ᐟ: "/",
};

/**
 * One spelling per unit notation, so that a unit typed the way the client
 * renders it (`nm⁻¹ᐟ²`, `J·s`) equals the authored one (`nm^(-1/2)`, `J s`):
 * NFC (Å as A + ring / U+212B), Greek mu → micro sign, superscripts → caret
 * exponents, `^(…)`/`^{…}` → `^…`, and multiplication separators → `·`.
 * Keeping the product boundary distinguishes `m·s` from milliseconds (`ms`).
 */
function normalizeUnitNotation(unit: string): string {
  return unit
    .trim()
    .normalize("NFC")
    .replace(/μ/g, "µ")
    .replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹⁻⁺ᐟ]+/g, (run) => `^${[...run].map((ch) => SUPERSCRIPTS[ch]).join("")}`)
    .replace(/\^\s*[({]\s*([^)}]*?)\s*[)}]/g, "^$1")
    .replace(/\s*([/()^])\s*/g, "$1")
    .replace(/[\s·⋅∙*×.]+/g, "·");
}

export function canonicalUnit(unit: string): string {
  // Common reciprocal spellings, without interpreting arbitrary unit algebra.
  const u = normalizeUnitNotation(unit).replace(
    /^1\/([\p{L}]+)(?:\^(\d+))?$/u,
    (_, base: string, power: string | undefined) => `${base}^-${power ?? "1"}`,
  );
  return UNIT_ALIASES[u] ?? UNIT_WORD_ALIASES[u.toLowerCase()] ?? u;
}

/**
 * Recognise prefix ambiguity even outside the finite conversion inventory:
 * pA/PA/Pa, Ms/ms, and t/T must never be guessed from lower-case spelling.
 * This registry guards case only; it does not add conversions for these units.
 */
const SI_PREFIXES = [
  "q",
  "r",
  "y",
  "z",
  "a",
  "f",
  "p",
  "n",
  "µ",
  "m",
  "c",
  "d",
  "da",
  "h",
  "k",
  "M",
  "G",
  "T",
  "P",
  "E",
  "Z",
  "Y",
  "R",
  "Q",
];
const PREFIXABLE_SYMBOLS = [
  "m",
  "g",
  "s",
  "A",
  "K",
  "mol",
  "cd",
  "rad",
  "sr",
  "Hz",
  "N",
  "Pa",
  "J",
  "W",
  "C",
  "V",
  "F",
  "Ω",
  "S",
  "Wb",
  "T",
  "H",
  "lm",
  "lx",
  "Bq",
  "Gy",
  "Sv",
  "kat",
  "eV",
];
const UNIT_SYMBOLS = new Map<string, string>();
for (const symbol of PREFIXABLE_SYMBOLS) {
  UNIT_SYMBOLS.set(symbol, "");
  for (const prefix of SI_PREFIXES) UNIT_SYMBOLS.set(prefix + symbol, prefix);
}
for (const symbol of ["c", "t", "min", "yr", "ly", "bar", "u", "Å", "deg"])
  UNIT_SYMBOLS.set(symbol, "");

const SYMBOLS_BY_LOWER = new Map<string, string[]>();
for (const symbol of UNIT_SYMBOLS.keys()) {
  const lower = symbol.toLowerCase();
  const symbols = SYMBOLS_BY_LOWER.get(lower) ?? [];
  symbols.push(symbol);
  SYMBOLS_BY_LOWER.set(lower, symbols);
}

/** Fold unit symbols only when both their meaning and prefix case are clear. */
function resolveUnitCase(unit: string): string | null {
  let valid = true;
  const resolved = unit.replace(/[\p{L}]+/gu, (symbol) => {
    if (UNIT_SYMBOLS.has(symbol)) return symbol;
    const candidates = SYMBOLS_BY_LOWER.get(symbol.toLowerCase());
    const candidate = candidates?.length === 1 ? candidates[0] : undefined;
    // Even an unsupported prefix spelling (NeV) must not become nano (neV).
    const prefix = candidate === undefined ? undefined : UNIT_SYMBOLS.get(candidate);
    if (candidate === undefined || prefix === undefined || !symbol.startsWith(prefix)) {
      valid = false;
      return symbol;
    }
    return candidate;
  });
  return valid ? canonicalUnit(resolved) : null;
}

/**
 * Converts `value` from unit `from` to unit `to` (canonical `UNIT_GROUPS`
 * keys on both sides). `null` when no single group contains both. The result
 * is rounded to 15 significant digits so that float noise from the factors
 * (`1 Å` → `0.09999999999999999 nm`) cannot fail an exact tolerance.
 */
function convertUnit(value: number, from: string, to: string): number | null {
  for (const group of UNIT_GROUPS) {
    const fromFactor = group[from];
    const toFactor = group[to];
    if (fromFactor !== undefined && toFactor !== undefined) {
      return Number(((value * fromFactor) / toFactor).toPrecision(15));
    }
  }
  return null;
}

/**
 * How a typed unit relates to a part's expected unit. Shared by
 * `scoreNumeric` (authoritative) and the client's live echo, so the two never
 * disagree about what a typed unit means:
 * - `"match"`: the same unit up to notation, alias or case — grade as is.
 * - `"convertible"`: a different unit in the same `UNIT_GROUPS` group —
 *   convert into the part's unit (`convertTypedUnit`) before grading.
 * - `"mismatch"`: incompatible or unknown — the part cannot be correct.
 *
 * Exact prefixes retain their scales (`meV/c` converts to `MeV/c`);
 * ambiguous case-folded spellings and changed prefix case are rejected.
 * Symbols in compounds follow the same prefix guard, even outside the groups.
 */
export function classifyTypedUnit(
  typedUnit: string,
  partUnit: string,
): "match" | "convertible" | "mismatch" {
  return convertTypedUnit(1, typedUnit, partUnit).kind;
}

type TypedUnitConversion =
  { kind: "match" | "convertible"; value: number } | { kind: "mismatch"; value: null };

function convertTypedUnit(value: number, typedUnit: string, partUnit: string): TypedUnitConversion {
  const typed = canonicalUnit(typedUnit);
  const part = canonicalUnit(partUnit);
  if (typed === part) return { kind: "match", value };
  // "0.98c" typed into a dimensionless v/c part states the same number.
  if (part === "" && typed === "c") return { kind: "match", value };
  const from = resolveUnitCase(typed);
  const to = resolveUnitCase(part);
  if (from !== null && to !== null) {
    if (from === to) return { kind: "match", value };
    const converted = convertUnit(value, from, to);
    if (converted !== null) return { kind: "convertible", value: converted };
  }
  return { kind: "mismatch", value: null };
}

/** The factors the generic detector looks for, as the `factor` it reports and the ratio it tests. */
const FACTORS: readonly (readonly [Extract<NearMiss, { kind: "factor" }>["factor"], number])[] = [
  [2, 2],
  [0.5, 0.5],
  ["2pi", 2 * Math.PI],
  ["1/2pi", 1 / (2 * Math.PI)],
];

/**
 * Explains a wrong-but-close answer. The score stays 0; this only drives the
 * feedback text. Returns null when the answer is correct or simply wrong.
 *
 * Detectors run in a fixed order and the first match wins: power-of-ten, unit,
 * sign, factor (pedagogy spec §5.3.3). A correct value and `answer === 0` have
 * no sign or factor near miss. A 10× miss is a power of ten, never a factor.
 */
export function nearMiss(value: number, part: NumericPartLike): NearMiss | null {
  if (!Number.isFinite(value)) return null;
  if (isNumericCorrect(value, part)) return null;

  // (a) right digits, wrong power of ten
  if (value !== 0 && part.answer !== 0) {
    const ratio = value / part.answer;
    if (ratio > 0) {
      const k = Math.round(Math.log10(ratio));
      if (k !== 0 && Math.abs(k) <= 12 && isNumericCorrect(value / 10 ** k, part)) {
        return { kind: "power-of-ten", exponent: k };
      }
    }
  }

  // (b) right value, wrong unit
  const to = canonicalUnit(part.unit);
  if (to !== "") {
    for (const group of UNIT_GROUPS) {
      const toFactor = group[to];
      if (toFactor === undefined) continue;
      for (const [from, fromFactor] of Object.entries(group)) {
        if (from === to || from === "") continue;
        const converted = (value * fromFactor) / toFactor;
        if (isNumericCorrect(converted, part)) return { kind: "unit", from, to };
      }
    }
  }

  if (part.answer !== 0) {
    // (c) right size, wrong sign
    if (isNumericCorrect(-value, part)) return { kind: "sign" };

    // (d) a factor 2, 1/2 or 2π too many or too few: `value / ratio` is graded by the
    // part's own tolerance, so the tolerance scales with the factor like the power of ten.
    for (const [factor, ratio] of FACTORS) {
      if (isNumericCorrect(value / ratio, part)) return { kind: "factor", factor };
    }
  }
  return null;
}

/* ---------------------------- authored errors ----------------------------- */

/** A numeric part as the diagnosis engine reads it: the graded fields plus its authored errors. */
export interface DiagnosablePart extends NumericPartLike {
  id: string;
  commonErrors?: readonly CommonError[];
}

/**
 * The inclusive window of values that count as an authored common error: the error's tolerance
 * (default: the part's own) applied around `error.value`, not around the answer. A relative
 * tolerance is a fraction of `error.value`, an absolute one is ± around it. An interval
 * tolerance inherited from the part keeps its offsets from the answer (the same width and
 * skew, moved onto `error.value`); an interval written on the error is taken as it stands.
 */
export function commonErrorWindow(
  error: Pick<CommonError, "value" | "tolerance">,
  part: NumericPartLike,
): { min: number; max: number } {
  const tolerance = error.tolerance ?? part.tolerance;
  const v = error.value;
  switch (tolerance.type) {
    case "relative": {
      const half = Math.abs(v) * tolerance.value * (1 + EPS) + EPS * Math.abs(v);
      return { min: v - half, max: v + half };
    }
    case "absolute": {
      const half = tolerance.value * (1 + EPS);
      return { min: v - half, max: v + half };
    }
    case "interval":
      return error.tolerance
        ? { min: tolerance.min - EPS, max: tolerance.max + EPS }
        : {
            min: v - (part.answer - tolerance.min) - EPS,
            max: v + (tolerance.max - part.answer) + EPS,
          };
  }
}

/**
 * The first authored common error (in the order they are written) whose window holds `value`,
 * which is the unit-converted value `scoreNumeric` grades. A value that is right for the part
 * never matches, and neither does a non-finite one.
 */
export function matchCommonError(value: number, part: DiagnosablePart): CommonError | null {
  if (!Number.isFinite(value) || isNumericCorrect(value, part)) return null;
  for (const error of part.commonErrors ?? []) {
    const { min, max } = commonErrorWindow(error, part);
    if (value >= min && value <= max) return error;
  }
  return null;
}

/* -------------------------------- scoring --------------------------------- */

export interface ScoreResult {
  score: number;
  maxScore: number;
  correct: boolean;
}

export interface NumericPartResult {
  id: string;
  raw: string;
  value: number | null;
  correct: boolean;
  /**
   * The generic near miss. Authored errors win: it is null whenever `commonError` is set, so a
   * reader never has two explanations to choose between.
   */
  nearMiss: NearMiss | null;
  /** The authored common error the (unit-converted) value matched, by id (`matchCommonError`). */
  commonError?: { id: string };
}

export interface NumericScoreResult extends ScoreResult {
  parts: NumericPartResult[];
}

export interface OpenScoreResult extends ScoreResult {
  /** Rubric ids that were accepted (unknown ids are dropped). */
  ticked: string[];
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** MC: full points when the selected set equals `correct`, else 0. */
export function scoreMc(question: McQuestion, selected: readonly string[]): ScoreResult {
  const chosen = new Set(selected);
  const expected = new Set(question.correct);
  const same = chosen.size === expected.size && [...expected].every((id) => chosen.has(id));
  return { score: same ? question.points : 0, maxScore: question.points, correct: same };
}

/** One part graded on its (unit-converted) value: tolerance, then the authored error, then near misses. */
function gradeValue(part: DiagnosablePart, raw: string, value: number): NumericPartResult {
  const correct = isNumericCorrect(value, part);
  const authored = correct ? null : matchCommonError(value, part);
  return {
    id: part.id,
    raw,
    value,
    correct,
    nearMiss: correct || authored ? null : nearMiss(value, part),
    ...(authored ? { commonError: { id: authored.id } } : {}),
  };
}

/** Numeric: `points × correctParts / totalParts`; correct only when every part is. */
export function scoreNumeric(
  question: NumericQuestion,
  answers: Readonly<Record<string, string | number | null | undefined>>,
): NumericScoreResult {
  const parts: NumericPartResult[] = question.parts.map((part) => {
    const raw = answers[part.id];
    const rawText = raw === null || raw === undefined ? "" : String(raw);
    const parsed = parseNumericAnswer(rawText);
    if ("error" in parsed) {
      return { id: part.id, raw: rawText, value: null, correct: false, nearMiss: null };
    }

    // No unit typed: unchanged behaviour — grade the bare value.
    if (parsed.unit === undefined) return gradeValue(part, rawText, parsed.value);

    // A unit was typed — it must not be silently ignored (review finding 3).
    const typedUnit = parsed.unit;
    const converted = convertTypedUnit(parsed.value, typedUnit, part.unit);
    if (converted.kind === "mismatch") {
      return {
        id: part.id,
        raw: rawText,
        value: parsed.value,
        correct: false,
        nearMiss: { kind: "unit-mismatch", typed: typedUnit, expected: part.unit },
      };
    }
    // "match" grades the value as typed; "convertible" grades it converted
    // into the part's unit — tolerance, the authored errors and the generic
    // near misses all apply to this graded value.
    return gradeValue(part, rawText, converted.value);
  });
  const correctCount = parts.filter((p) => p.correct).length;
  const score = round2((question.points * correctCount) / question.parts.length);
  return {
    score,
    maxScore: question.points,
    correct: correctCount === question.parts.length,
    parts,
  };
}

/** A slip stored with a wrong numeric part (pedagogy spec §2.2 `slips`). */
export interface PartSlip {
  part: string;
  kind: SlipKind;
  /** The authored common error behind it, when there is one. */
  errorId?: string;
}

/**
 * The slip behind a wrong numeric part (pedagogy spec §3.3.2, §5.3.3). Precedence, highest first:
 *
 * 1. the part's authored common error (`partResult.commonError`): its `slip` and `errorId`;
 * 2. the generic near misses, in the order `nearMiss()` finds them: `power-of-ten`; `unit` and
 *    `unit-mismatch` (both `unit`); `sign`; `factor`.
 *
 * A right part, an unreadable answer or a plainly wrong value has none. An authored error that
 * matched but is no longer on the part (a stale result) falls through to the generic kinds.
 */
export function slipOf(partResult: NumericPartResult, part: DiagnosablePart): PartSlip | null {
  if (partResult.correct) return null;
  if (partResult.commonError) {
    const authored = part.commonErrors?.find((error) => error.id === partResult.commonError?.id);
    if (authored) return { part: part.id, kind: authored.slip, errorId: authored.id };
  }
  if (partResult.nearMiss === null) return null;
  switch (partResult.nearMiss.kind) {
    case "power-of-ten":
      return { part: part.id, kind: "power-of-ten" };
    case "unit":
    case "unit-mismatch":
      return { part: part.id, kind: "unit" };
    case "sign":
      return { part: part.id, kind: "sign" };
    case "factor":
      return { part: part.id, kind: "factor" };
  }
}

/** A slip stored with a wrong multiple-choice option (pedagogy spec §2.2 `slips`). */
export interface OptionSlip {
  option: string;
  kind: SlipKind;
}

/**
 * The option a wrong single-select answer picked, or `undefined`: a multi-select question, a
 * selection of anything but one option, an unknown option and the right option have none
 * (pedagogy spec §5.3.3: multi-select gets the generic message only).
 */
function chosenWrongOption(
  question: McQuestion,
  selected: readonly string[],
): McQuestion["options"][number] | undefined {
  const ids = new Set(selected);
  if (question.multiple || ids.size !== 1) return undefined;
  const [id] = ids;
  if (question.correct.includes(id)) return undefined;
  return question.options.find((option) => option.id === id);
}

/** The slip authored on the wrong option a single-select answer picked, if any. */
export function mcSlipOf(question: McQuestion, selected: readonly string[]): OptionSlip | null {
  const option = chosenWrongOption(question, selected);
  return option?.slip ? { option: option.id, kind: option.slip } : null;
}

/** What a wrong single-select option says about the student's mistake (`diagnosis`, localized). */
export interface McDiagnosis {
  optionId: string;
  message: Localized;
}

/** The diagnosis authored on the wrong option a single-select answer picked, if any. */
export function mcDiagnosisOf(
  question: McQuestion,
  selected: readonly string[],
): McDiagnosis | null {
  const option = chosenWrongOption(question, selected);
  return option?.diagnosis ? { optionId: option.id, message: option.diagnosis } : null;
}

/** Open: sum of ticked rubric points (self-graded), capped at the item's points. */
export function scoreOpen(
  question: OpenQuestion,
  tickedRubricIds: readonly string[],
): OpenScoreResult {
  const valid = new Map(question.rubric.map((r) => [r.id, r.points]));
  const ticked: string[] = [];
  const seen = new Set<string>();
  for (const id of tickedRubricIds) {
    if (!valid.has(id) || seen.has(id)) continue;
    seen.add(id);
    ticked.push(id);
  }
  const raw = ticked.reduce((sum, id) => sum + (valid.get(id) ?? 0), 0);
  const score = round2(Math.min(raw, question.points));
  return {
    score,
    maxScore: question.points,
    correct: score >= 0.5 * question.points,
    ticked,
  };
}

/** Max score of any item — always its `points` (spec §4.4). */
export function maxScoreFor(question: Question): number {
  return question.points;
}
